class StickyColumn {
  constructor(element, { offset = 44, minWidth = 768 } = {}) {
    this.element = element;
    this.offset = offset;
    this.minWidth = minWidth;
    this.lastScrollY = 0;
    this.stickyTop = offset;
    this.rafId = 0;
    this.lenisUnsubscribe = null;
    this.useWindowScroll = false;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.init();
  }

  getLenis() {
    return typeof window !== 'undefined' ? window.lenis ?? null : null;
  }

  getScrollY() {
    const lenis = this.getLenis();
    if (lenis && typeof lenis.scroll === 'number') {
      return lenis.scroll;
    }

    return window.scrollY;
  }

  getElementHeight() {
    if (!this.element) return 0;
    return this.element.offsetHeight;
  }

  getBounds() {
    const elementHeight = this.getElementHeight();
    const viewportHeight = window.innerHeight;
    const fitsViewport = elementHeight <= viewportHeight - this.offset;

    return {
      elementHeight,
      viewportHeight,
      fitsViewport,
      minTop: -(elementHeight - viewportHeight),
      maxTop: this.offset,
    };
  }

  init() {
    if (!this.element) return;

    this.lastScrollY = this.getScrollY();
    this.stickyTop = this.offset;
    this.apply();

    const lenis = this.getLenis();
    if (lenis && typeof lenis.on === 'function') {
      this.lenisUnsubscribe = lenis.on('scroll', this.onScroll);
    } else {
      this.useWindowScroll = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    window.addEventListener('resize', this.onResize);
  }

  clear() {
    if (!this.element) return;
    this.element.style.position = '';
    this.element.style.top = '';
    this.element.style.willChange = '';
  }

  apply() {
    if (!this.element) return;

    if (window.innerWidth < this.minWidth) {
      this.clear();
      return;
    }

    const { fitsViewport, minTop, maxTop } = this.getBounds();

    if (fitsViewport) {
      this.stickyTop = this.offset;
      this.element.style.position = 'sticky';
      this.element.style.top = `${this.offset}px`;
      this.element.style.willChange = '';
      return;
    }

    this.stickyTop = Math.max(minTop, Math.min(maxTop, this.stickyTop));
    this.element.style.position = 'sticky';
    this.element.style.top = `${this.stickyTop}px`;
    this.element.style.willChange = 'top';
  }

  onScroll() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0;
      this.updateFromScroll();
    });
  }

  updateFromScroll() {
    if (!this.element) return;

    if (window.innerWidth < this.minWidth) {
      this.clear();
      this.lastScrollY = this.getScrollY();
      return;
    }

    const scrollY = this.getScrollY();
    const scrollDelta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;

    const { fitsViewport, minTop, maxTop } = this.getBounds();

    if (fitsViewport) {
      this.stickyTop = this.offset;
      this.apply();
      return;
    }

    if (scrollDelta === 0) return;

    if (scrollDelta > 0) {
      this.stickyTop = Math.max(minTop, this.stickyTop - scrollDelta);
    } else {
      this.stickyTop = Math.min(maxTop, this.stickyTop + Math.abs(scrollDelta));
    }

    this.apply();
  }

  clampStickyTop() {
    const { fitsViewport, minTop, maxTop } = this.getBounds();

    if (fitsViewport) {
      this.stickyTop = this.offset;
      return;
    }

    this.stickyTop = Math.max(minTop, Math.min(maxTop, this.stickyTop));
  }

  refresh() {
    const lenis = this.getLenis();
    if (lenis && typeof lenis.resize === 'function') {
      lenis.resize();
    }

    requestAnimationFrame(() => {
      this.clampStickyTop();
      this.lastScrollY = this.getScrollY();
      this.apply();
    });
  }

  onResize() {
    this.lastScrollY = this.getScrollY();
    this.clampStickyTop();
    this.apply();
  }

  destroy() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    if (this.useWindowScroll) {
      window.removeEventListener('scroll', this.onScroll);
    }

    if (this.lenisUnsubscribe) {
      this.lenisUnsubscribe();
      this.lenisUnsubscribe = null;
    }

    window.removeEventListener('resize', this.onResize);
    this.clear();
  }
}

export default StickyColumn;
